import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Edit,
  Store,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Phone,
} from "lucide-react";
import {
  getShops,
  createShop,
  updateShop,
  canManageShops,
} from "../lib/pocketbase";
import { Shop, ShopForm } from "../types";
import { formatPersianDate } from "../lib/utils";
import {
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "../components/ui/Table";
import Input from "../components/ui/Input";
import Checkbox from "../components/ui/Checkbox";
import { Navigate } from "react-router-dom";

const ShopsPage: React.FC = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingShop, setEditingShop] = useState<Shop | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);

  const [formData, setFormData] = useState<ShopForm>({
    email: "",
    password: "",
    is_active: true,
    title: "",
    address: "",
    phone: "",
    slug: "",
  });

  const [errors, setErrors] = useState<Partial<ShopForm>>({});

  useEffect(() => {
    fetchShops();
  }, [currentPage]);

  const fetchShops = useCallback(async () => {
    try {
      const response = await getShops(currentPage, perPage);
      setShops(response.items);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      if (error?.message?.includes("autocancelled")) {
        return;
      }
      console.error("Error fetching shops:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage]);

  if (!canManageShops()) {
    return <Navigate to="/" replace />;
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<ShopForm> = {};

    if (!editingShop) {
      if (!formData.email) {
        newErrors.email = "ایمیل الزامی است";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "ایمیل معتبر نیست";
      }

      if (!formData.password) {
        newErrors.password = "رمز عبور الزامی است";
      }
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = "رمز عبور باید حداقل 8 کاراکتر باشد";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      if (editingShop) {
        const { email: _email, password, ...rest } = formData;
        const updateData: Partial<ShopForm> = { ...rest };
        if (password.trim()) {
          updateData.password = password;
        }
        await updateShop(editingShop.id, updateData);
      } else {
        await createShop(formData);
      }

      setShowModal(false);
      setEditingShop(null);
      resetForm();
      fetchShops();
    } catch (error: any) {
      console.error("Error saving shop:", error);
      if (error.data?.email?.message) {
        setErrors({ email: error.data.email.message });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (shop: Shop) => {
    setEditingShop(shop);
    setFormData({
      email: shop.email,
      password: "",
      is_active: shop.is_active === true,
      title: shop.title || "",
      address: shop.address || "",
      phone: shop.phone || "",
      slug: shop.slug || "",
    });
    setErrors({});
    setShowModal(true);
  };

  const handleToggleActive = async (shop: Shop) => {
    try {
      await updateShop(shop.id, { is_active: !shop.is_active });
      fetchShops();
    } catch (error) {
      console.error("Error toggling shop access:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      is_active: true,
      title: "",
      address: "",
      phone: "",
      slug: "",
    });
    setErrors({});
  };

  if (loading && shops.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            مدیریت فروشگاه‌ها
          </h1>
          <p className="text-gray-600">
            ایجاد، ویرایش و فعال‌سازی دسترسی فروشندگان
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingShop(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center justify-center w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 ml-2" />
          افزودن فروشگاه
        </button>
      </div>

      <div className="card">
        <Table>
          <TableHeader>
            <TableHeaderCell>عنوان</TableHeaderCell>
            <TableHeaderCell>ایمیل</TableHeaderCell>
            <TableHeaderCell>تلفن</TableHeaderCell>
            <TableHeaderCell>دسترسی</TableHeaderCell>
            <TableHeaderCell>تاریخ ایجاد</TableHeaderCell>
            <TableHeaderCell>عملیات</TableHeaderCell>
          </TableHeader>
          <tbody className="bg-white divide-y divide-gray-200">
            {shops.map((shop) => (
              <TableRow key={shop.id}>
                <TableCell>
                  <div className="flex items-center">
                    <Store className="h-4 w-4 text-gray-400 ml-2" />
                    <span className="font-medium text-gray-900">
                      {shop.title || "—"}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-gray-400 ml-2" />
                    <span className="text-gray-900">{shop.email}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 text-gray-400 ml-2" />
                    <span className="text-sm text-gray-600">
                      {shop.phone || "—"}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => handleToggleActive(shop)}
                    className="flex items-center hover:opacity-80 transition-opacity"
                    title={
                      shop.is_active
                        ? "غیرفعال کردن دسترسی"
                        : "فعال کردن دسترسی"
                    }
                  >
                    {shop.is_active ? (
                      <CheckCircle className="h-4 w-4 text-green-500 ml-1" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500 ml-1" />
                    )}
                    <span
                      className={`text-sm ${
                        shop.is_active ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {shop.is_active ? "فعال" : "مسدود"}
                    </span>
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 ml-1" />
                    <span className="text-sm text-gray-500">
                      {formatPersianDate(shop.created)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <button
                      onClick={() => handleEdit(shop)}
                      className="p-1 text-primary-600 hover:text-primary-700 transition-colors"
                      title="ویرایش"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </div>

      {shops.length === 0 && !loading && (
        <div className="text-center py-12">
          <Store className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            هیچ فروشگاهی یافت نشد
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            فروشگاه جدید اضافه کنید
          </p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center overflow-x-auto">
          <nav className="flex items-center space-x-2 space-x-reverse">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-secondary disabled:opacity-50"
            >
              قبلی
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg shrink-0 ${
                    page === currentPage
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                  }`}
                >
                  {page}
                </button>
              )
            )}

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="btn-secondary disabled:opacity-50"
            >
              بعدی
            </button>
          </nav>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-[calc(100%-2rem)] max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingShop ? "ویرایش فروشگاه" : "افزودن فروشگاه جدید"}
              </h3>

              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                autoComplete="off"
                noValidate
              >
                <Input
                  label="عنوان فروشگاه"
                  type="text"
                  name="shop-title"
                  autoComplete="off"
                  value={formData.title || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                />

                {editingShop ? (
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ایمیل
                    </label>
                    <div
                      className="input-field h-[42px] min-h-[42px] box-border flex items-center text-sm text-gray-700 bg-gray-50 cursor-default"
                    >
                      {editingShop.email || formData.email}
                    </div>
                  </div>
                ) : (
                  <Input
                    label="ایمیل"
                    type="email"
                    name="shop-email"
                    autoComplete="off"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    error={errors.email}
                    required
                  />
                )}

                <Input
                  label={
                    editingShop ? "رمز عبور جدید (اختیاری)" : "رمز عبور"
                  }
                  type="password"
                  name="shop-password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  error={errors.password}
                  required={!editingShop}
                  helperText={
                    editingShop
                      ? "برای تغییر رمز عبور، فیلد را پر کنید"
                      : "حداقل 8 کاراکتر"
                  }
                />

                <Input
                  label="تلفن"
                  type="text"
                  name="shop-phone"
                  autoComplete="off"
                  value={formData.phone || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />

                <Input
                  label="آدرس"
                  type="text"
                  name="shop-address"
                  autoComplete="off"
                  value={formData.address || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />

                <Input
                  label="اسلاگ"
                  type="text"
                  name="shop-slug"
                  autoComplete="off"
                  value={formData.slug || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                />

                <Checkbox
                  label="دسترسی فعال — فروشنده می‌تواند وارد پنل شود"
                  checked={formData.is_active}
                  onChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />

                <div className="flex justify-end space-x-3 space-x-reverse pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingShop(null);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    انصراف
                  </button>
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving
                      ? "در حال ذخیره..."
                      : editingShop
                        ? "ویرایش"
                        : "افزودن"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopsPage;
